import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, descargar } from "../lib/api";
import { creditos, fecha, fechaHora, gramos, iso, mgAGramos } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, Vacio } from "../componentes/piezas";
import Suscripcion from "../componentes/Suscripcion";
import { useSesion } from "../lib/sesion";
import { IDIOMAS, useIdioma, type Idioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Club {
  id: string;
  nombre: string;
  cif: string | null;
  direccion: string | null;
  ciudad: string | null;
  provincia: string | null;
  cp: string | null;
  telefono: string | null;
  email: string | null;
  limiteDiarioMg: number;
  limiteMensualMg: number;
  cuotaAnualCentimos: number;
  ajustes: Record<string, unknown> & {
    textoTicket?: string;
    avisoLegal?: string;
    anchoTicketMm?: number;
    avisosCuota?: { activo?: boolean; dias?: number };
    aforo?: number;
    /** El idioma en que el club habla a sus socios: portal y correos. */
    idioma?: Idioma;
  };
}

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  ultimoAcceso: string | null;
  permisosExtra: string;
  permisosQuitados: string;
  puestoId: string | null;
  puesto: { id: string; nombre: string } | null;
}

interface Puesto {
  id: string;
  nombre: string;
  activo: boolean;
  _count: { usuarios: number };
}

interface PermisoCatalogo {
  clave: string;
  etiqueta: string;
  rolMinimo: string;
}

const RANGO_ROL: Record<string, number> = { TRABAJADOR: 1, PROPIETARIO: 2 };
const listaJson = (json: string): string[] => {
  try {
    const v = JSON.parse(json || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

interface TipoSocio {
  id: string;
  nombre: string;
  descripcion: string | null;
  descuentoPorcentaje: number;
  cuotaAnualCentimos: number | null;
  duracionMeses: number;
  limiteDiarioMg: number | null;
  limiteMensualMg: number | null;
  color: string;
  orden: number;
  activo: boolean;
  _count: { socios: number };
}

interface Auditoria {
  id: string;
  fecha: string;
  accion: string;
  entidad: string | null;
  usuario: { nombre: string } | null;
}

export default function Ajustes() {
  const { t, cuenta, dias: enDias } = useIdioma();
  const clienteConsultas = useQueryClient();
  const { puede, salir, refrescar: refrescarSesion } = useSesion();
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [nuevoUsuario, setNuevoUsuario] = useState(false);
  const [cerrandoSesiones, setCerrandoSesiones] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState<Usuario | null>(null);
  const [editandoTipo, setEditandoTipo] = useState<TipoSocio | "nuevo" | null>(null);

  const club = useQuery({ queryKey: ["club"], queryFn: () => api<Club>("/club") });
  const usuarios = useQuery({
    queryKey: ["usuarios"],
    queryFn: () => api<Usuario[]>("/club/usuarios"),
    enabled: puede("gestionarUsuarios"),
  });
  const auditoria = useQuery({
    queryKey: ["auditoria"],
    queryFn: () => api<Auditoria[]>("/club/auditoria", { query: { limite: 40 } }),
    enabled: puede("verAuditoria"),
  });
  const tipos = useQuery({ queryKey: ["tipos-socio"], queryFn: () => api<TipoSocio[]>("/tipos-socio") });
  const puestos = useQuery({ queryKey: ["puestos"], queryFn: () => api<Puesto[]>("/club/puestos") });
  const estadoCopia = useQuery({
    queryKey: ["copia-estado"],
    queryFn: () => api<{ sqlite: boolean; clubesEnLaInstalacion: number }>("/club/copia-seguridad/estado"),
    enabled: puede("copiaSeguridad"),
  });
  const copiasAuto = useQuery({
    queryKey: ["copias-automaticas"],
    queryFn: () => api<{ nombre: string; fecha: string; bytes: number }[]>("/club/copia-seguridad/automaticas"),
    enabled: puede("copiaSeguridad"),
  });

  const [datos, setDatos] = useState({
    nombre: "",
    cif: "",
    direccion: "",
    ciudad: "",
    provincia: "",
    cp: "",
    telefono: "",
    email: "",
    limiteDiarioG: "",
    limiteMensualG: "",
    cuotaAnualEuros: "",
    textoTicket: "",
    avisoLegal: "",
    anchoTicketMm: "80",
    idioma: "es",
    avisosCuotaActivo: false,
    avisosCuotaDias: "15",
    aforo: "0",
  });

  useEffect(() => {
    if (!club.data) return;
    setDatos({
      nombre: club.data.nombre,
      cif: club.data.cif ?? "",
      direccion: club.data.direccion ?? "",
      ciudad: club.data.ciudad ?? "",
      provincia: club.data.provincia ?? "",
      cp: club.data.cp ?? "",
      telefono: club.data.telefono ?? "",
      email: club.data.email ?? "",
      limiteDiarioG: String(mgAGramos(club.data.limiteDiarioMg)),
      limiteMensualG: String(mgAGramos(club.data.limiteMensualMg)),
      cuotaAnualEuros: String(club.data.cuotaAnualCentimos / 100),
      textoTicket: club.data.ajustes?.textoTicket ?? "",
      avisoLegal: club.data.ajustes?.avisoLegal ?? "",
      anchoTicketMm: String(club.data.ajustes?.anchoTicketMm ?? 80),
      idioma: club.data.ajustes?.idioma ?? "es",
      avisosCuotaActivo: club.data.ajustes?.avisosCuota?.activo === true,
      avisosCuotaDias: String(club.data.ajustes?.avisosCuota?.dias ?? 15),
      aforo: String(club.data.ajustes?.aforo ?? 0),
    });
  }, [club.data]);

  const cambiar = (campo: string, valor: string | boolean) => setDatos((d) => ({ ...d, [campo]: valor }));

  async function guardar() {
    setError("");
    setMensaje("");
    try {
      await api("/club", {
        metodo: "PATCH",
        cuerpo: {
          ...datos,
          limiteDiarioG: Number(datos.limiteDiarioG),
          limiteMensualG: Number(datos.limiteMensualG),
          cuotaAnualEuros: Number(datos.cuotaAnualEuros),
          ajustes: {
            ...(club.data?.ajustes ?? {}),
            textoTicket: datos.textoTicket,
            avisoLegal: datos.avisoLegal,
            anchoTicketMm: Number(datos.anchoTicketMm),
            idioma: datos.idioma,
            avisosCuota: { activo: datos.avisosCuotaActivo, dias: Number(datos.avisosCuotaDias) },
            aforo: Number(datos.aforo) || 0,
          },
        },
      });
      setMensaje(t("aj.guardados"));
      void clienteConsultas.invalidateQueries({ queryKey: ["club"] });
      void refrescarSesion();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("aj.fallo"));
    }
  }

  if (club.isLoading) return <Cargando />;

  return (
    <>
      <Cabecera titulo={t("aj.titulo")} descripcion={t("aj.descripcion")}>
        <button className="boton" onClick={guardar}>
          {t("aj.guardarCambios")}
        </button>
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      <div className="rejilla dos" style={{ alignItems: "start" }}>
        <div className="columna" style={{ gap: 16 }}>
          <Panel titulo={t("aj.datos")}>
            <Campo etiqueta={t("aj.nombre")}>
              <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} />
            </Campo>
            <div className="linea-campos">
              <Campo etiqueta={t("aj.cif")}>
                <input value={datos.cif} onChange={(e) => cambiar("cif", e.target.value)} />
              </Campo>
              <Campo etiqueta={t("aj.telefono")}>
                <input value={datos.telefono} onChange={(e) => cambiar("telefono", e.target.value)} />
              </Campo>
            </div>
            <Campo etiqueta={t("aj.direccion")}>
              <input value={datos.direccion} onChange={(e) => cambiar("direccion", e.target.value)} />
            </Campo>
            <div className="linea-campos">
              <Campo etiqueta={t("aj.ciudad")}>
                <input value={datos.ciudad} onChange={(e) => cambiar("ciudad", e.target.value)} />
              </Campo>
              <Campo etiqueta={t("aj.provincia")}>
                <input value={datos.provincia} onChange={(e) => cambiar("provincia", e.target.value)} />
              </Campo>
              <Campo etiqueta={t("aj.cp")}>
                <input value={datos.cp} onChange={(e) => cambiar("cp", e.target.value)} />
              </Campo>
            </div>
            <Campo etiqueta={t("aj.email")}>
              <input type="email" value={datos.email} onChange={(e) => cambiar("email", e.target.value)} />
            </Campo>
          </Panel>

          <Panel titulo={t("aj.reglas")}>
            <div className="linea-campos">
              <Campo etiqueta={t("aj.limiteDiario")} ayuda={t("aj.limiteDiarioAyuda")}>
                <input
                  type="number"
                  step="0.5"
                  min={0}
                  value={datos.limiteDiarioG}
                  onChange={(e) => cambiar("limiteDiarioG", e.target.value)}
                />
              </Campo>
              <Campo etiqueta={t("aj.limiteMensual")} ayuda={t("aj.limiteMensualAyuda")}>
                <input
                  type="number"
                  step="1"
                  min={0}
                  value={datos.limiteMensualG}
                  onChange={(e) => cambiar("limiteMensualG", e.target.value)}
                />
              </Campo>
            </div>
            <Campo etiqueta={t("aj.cuotaAnual")}>
              <input
                type="number"
                step="0.01"
                min={0}
                value={datos.cuotaAnualEuros}
                onChange={(e) => cambiar("cuotaAnualEuros", e.target.value)}
              />
            </Campo>
            <p className="suave pequeno">{t("aj.limitesPropios")}</p>

            <Campo etiqueta={t("aj.aforo")} ayuda={t("aj.aforoAyuda")}>
              <input
                type="number"
                min={0}
                max={2000}
                value={datos.aforo}
                onChange={(e) => cambiar("aforo", e.target.value)}
              />
            </Campo>
            <p className="suave pequeno" style={{ marginBottom: 0 }}>
              {t("aj.aforoNota")}
            </p>
          </Panel>

          <Panel titulo={t("aj.ticket")}>
            <Campo etiqueta={t("aj.idiomaPortal")} ayuda={t("aj.idiomaPortalAyuda")}>
              {/* De la lista de idiomas, no escritos a mano: asi añadir uno no
                  obliga a acordarse de esta pantalla. El nombre va en su propio
                  idioma, que es como se reconoce. */}
              <select value={datos.idioma} onChange={(e) => cambiar("idioma", e.target.value)}>
                {IDIOMAS.map((i) => (
                  <option key={i.codigo} value={i.codigo} lang={i.codigo}>
                    {i.nombre}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta={t("aj.anchoPapel")} ayuda={t("aj.anchoPapelAyuda")}>
              <select value={datos.anchoTicketMm} onChange={(e) => cambiar("anchoTicketMm", e.target.value)}>
                <option value="80">{t("aj.ancho80")}</option>
                <option value="58">{t("aj.ancho58")}</option>
              </select>
            </Campo>
            <Campo etiqueta={t("aj.textoTicket")} ayuda={t("aj.textoTicketAyuda")}>
              <textarea rows={2} value={datos.textoTicket} onChange={(e) => cambiar("textoTicket", e.target.value)} />
            </Campo>
            <Campo etiqueta={t("aj.avisoLegal")}>
              <textarea rows={2} value={datos.avisoLegal} onChange={(e) => cambiar("avisoLegal", e.target.value)} />
            </Campo>
          </Panel>

          <Panel titulo={t("aj.avisoCuota")}>
            <p className="suave pequeno" style={{ marginTop: 0 }}>
              {t("aj.avisoCuotaNota")}
            </p>

            <label className="fila" style={{ gap: 8, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={datos.avisosCuotaActivo}
                onChange={(e) => cambiar("avisosCuotaActivo", e.target.checked)}
              />
              <span>{t("aj.avisarAuto")}</span>
            </label>

            <Campo etiqueta={t("aj.cuantosDiasAntes")} ayuda={t("aj.cuantosDiasAyuda")}>
              <select
                value={datos.avisosCuotaDias}
                onChange={(e) => cambiar("avisosCuotaDias", e.target.value)}
                disabled={!datos.avisosCuotaActivo}
              >
                {[7, 15, 30, 60].map((d) => (
                  <option key={d} value={d}>
                    {enDias(d)}
                  </option>
                ))}
              </select>
            </Campo>

            <p className="suave pequeno" style={{ marginBottom: 0 }}>
              {t("aj.avisoApagado")}
            </p>
          </Panel>
        </div>

        <div className="columna" style={{ gap: 16 }}>
          {puede("ajustesClub") && (
            <PuestosDeTrabajo
              puestos={puestos.data ?? []}
              alFallar={setError}
              alHecho={() => {
                void clienteConsultas.invalidateQueries({ queryKey: ["puestos"] });
                void clienteConsultas.invalidateQueries({ queryKey: ["usuarios"] });
              }}
            />
          )}

          {puede("gestionarUsuarios") && (
            <Panel
              titulo={t("aj.equipo")}
              acciones={
                <button className="boton secundario" onClick={() => setNuevoUsuario(true)}>
                  {t("aj.nuevoUsuario")}
                </button>
              }
              sinCuerpo
            >
              {usuarios.isLoading ? (
                <Cargando />
              ) : (
                <div className="tabla-envoltorio">
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th>{t("aj.usuario")}</th>
                        <th>{t("aj.rol")}</th>
                        <th>{t("aj.ultimoAcceso")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(usuarios.data ?? []).map((u) => (
                        <tr key={u.id}>
                          <td>
                            <strong>{u.nombre}</strong>
                            <div className="suave pequeno">{u.email}</div>
                          </td>
                          <td>
                            <Etiqueta tipo={u.activo ? "verde" : ""}>{t(`rol.${u.rol}` as Clave)}</Etiqueta>
                            {!u.activo && (
                              <span className="etiqueta roja" style={{ marginLeft: 6 }}>
                                {t("aj.desactivado")}
                              </span>
                            )}
                          </td>
                          <td className="pequeno suave">
                            {u.ultimoAcceso ? fechaHora(u.ultimoAcceso) : t("aj.nunca")}
                            {(listaJson(u.permisosExtra).length > 0 || listaJson(u.permisosQuitados).length > 0) && (
                              <div>
                                <Etiqueta tipo="azul">{t("aj.permisosAMedida")}</Etiqueta>
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button className="boton fantasma" onClick={() => setEditandoUsuario(u)}>
                              {t("aj.editar")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          )}

          {puede("gestionarUsuarios") && (
            <Panel titulo={t("aj.cs.titulo")}>
              <p className="suave pequeno" style={{ margin: "0 0 12px" }}>
                {t("aj.cs.explicacion")}
              </p>
              <button className="boton secundario" onClick={() => setCerrandoSesiones(true)}>
                {t("aj.cs.boton")}
              </button>
            </Panel>
          )}

          <Panel
            titulo={t("aj.tiposDeSocio")}
            acciones={
              <button className="boton secundario" onClick={() => setEditandoTipo("nuevo")}>
                {t("aj.nuevoTipo")}
              </button>
            }
            sinCuerpo
          >
            {tipos.isLoading ? (
              <Cargando />
            ) : !tipos.data || tipos.data.length === 0 ? (
              <Vacio>{t("aj.sinTipos")}</Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>{t("aj.tipo")}</th>
                      <th className="num">{t("aj.descuento")}</th>
                      <th className="num">{t("aj.cuota")}</th>
                      <th>{t("aj.dura")}</th>
                      <th>{t("aj.limites")}</th>
                      <th className="num">{t("aj.socios")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {tipos.data.map((tipo) => (
                      <tr key={tipo.id} style={tipo.activo ? undefined : { opacity: 0.55 }}>
                        <td>
                          <span
                            className="etiqueta"
                            style={{ background: `${tipo.color}20`, color: tipo.color }}
                          >
                            {tipo.nombre}
                          </span>
                          {tipo.descripcion && <div className="suave pequeno">{tipo.descripcion}</div>}
                        </td>
                        <td className="num tabular">
                          {tipo.descuentoPorcentaje > 0 ? `−${tipo.descuentoPorcentaje}%` : "—"}
                        </td>
                        <td className="num tabular">
                          {tipo.cuotaAnualCentimos == null ? (
                            <span className="suave">{t("aj.laDelClub")}</span>
                          ) : (
                            creditos(tipo.cuotaAnualCentimos)
                          )}
                        </td>
                        <td className="pequeno">
                          {tipo.duracionMeses === 12
                            ? t("cu.unAnio")
                            : cuenta(tipo.duracionMeses, "cu.unMes", "cu.meses")}
                        </td>
                        <td className="pequeno">
                          {tipo.limiteDiarioMg == null && tipo.limiteMensualMg == null ? (
                            <span className="suave">{t("aj.losDelClub")}</span>
                          ) : (
                            t("aj.limitesDiaMes", {
                              diario: tipo.limiteDiarioMg == null ? "—" : gramos(tipo.limiteDiarioMg, 1),
                              mensual: tipo.limiteMensualMg == null ? "—" : gramos(tipo.limiteMensualMg, 0),
                            })
                          )}
                        </td>
                        <td className="num tabular">{tipo._count.socios}</td>
                        <td style={{ textAlign: "right" }}>
                          <button className="boton fantasma" onClick={() => setEditandoTipo(tipo)}>
                            {t("aj.editar")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {puede("verAuditoria") && (
            <Panel titulo={t("aj.registro")} sinCuerpo>
              {auditoria.isLoading ? (
                <Cargando />
              ) : !auditoria.data || auditoria.data.length === 0 ? (
                <Vacio>{t("aj.sinRegistros")}</Vacio>
              ) : (
                <div className="tabla-envoltorio" style={{ maxHeight: 420, overflowY: "auto" }}>
                  <table className="tabla">
                    <tbody>
                      {auditoria.data.map((a) => (
                        <tr key={a.id}>
                          <td className="pequeno suave" style={{ whiteSpace: "nowrap" }}>
                            {fechaHora(a.fecha)}
                          </td>
                          <td className="pequeno">
                            <strong>{a.accion.replaceAll("_", " ").toLowerCase()}</strong>
                            <div className="suave">{a.usuario?.nombre ?? t("aj.sistema")}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          )}

          <Suscripcion />

          {puede("copiaSeguridad") && (
            <Panel titulo={t("aj.copia")}>
              <p className="suave pequeno" style={{ marginTop: 0 }}>
                {t("aj.copiaNota")}
              </p>
              <div className="fila ajustable">
                <button
                  className="boton secundario"
                  onClick={() =>
                    descargar("/club/copia-seguridad/datos.json", `copia-datos-${iso(new Date())}.json`).catch((fallo) =>
                      setError(fallo instanceof Error ? fallo.message : t("aj.falloCopia")),
                    )
                  }
                >
                  {t("aj.copiaDatos")}
                </button>
                {estadoCopia.data?.sqlite && estadoCopia.data.clubesEnLaInstalacion === 1 && (
                  <button
                    className="boton secundario"
                    onClick={() =>
                      descargar("/club/copia-seguridad/base.db", `copia-base-${iso(new Date())}.db`).catch((fallo) =>
                        setError(fallo instanceof Error ? fallo.message : t("aj.falloCopia")),
                      )
                    }
                  >
                    {t("aj.copiaBase")}
                  </button>
                )}
              </div>
              {estadoCopia.data && estadoCopia.data.clubesEnLaInstalacion > 1 && (
                <p className="suave pequeno">{t("aj.variosClubes")}</p>
              )}

              {/* El club se olvida de pulsar el botón, así que el programa las hace solo cada noche. */}
              <h3 style={{ marginBottom: 4 }}>{t("aj.copiasAuto")}</h3>
              {copiasAuto.data && copiasAuto.data.length > 0 ? (
                <>
                  <p className="suave pequeno" style={{ marginTop: 0 }}>
                    {t("aj.copiasAutoNota")}
                  </p>
                  <div className="lista-copias">
                    {copiasAuto.data.map((copia) => (
                      <button
                        key={copia.nombre}
                        className="boton fantasma"
                        onClick={() =>
                          descargar(
                            `/club/copia-seguridad/automaticas/${copia.nombre}`,
                            copia.nombre,
                          ).catch((fallo) =>
                            setError(fallo instanceof Error ? fallo.message : t("aj.falloCopia")),
                          )
                        }
                      >
                        <span>{fecha(`${copia.fecha}T12:00:00`)}</span>
                        <span className="suave pequeno">{Math.max(1, Math.round(copia.bytes / 1024))} KB</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="suave pequeno" style={{ marginTop: 0, marginBottom: 0 }}>
                  {t("aj.sinCopiasAun")}
                </p>
              )}
            </Panel>
          )}
        </div>
      </div>

      {editandoTipo && (
        <DialogoTipo
          tipo={editandoTipo === "nuevo" ? null : editandoTipo}
          alCerrar={() => setEditandoTipo(null)}
          alFallar={setError}
          alHecho={() => {
            setEditandoTipo(null);
            void clienteConsultas.invalidateQueries({ queryKey: ["tipos-socio"] });
          }}
        />
      )}

      {editandoUsuario && (
        <DialogoUsuarioEditar
          usuario={editandoUsuario}
          alCerrar={() => setEditandoUsuario(null)}
          alFallar={setError}
          alHecho={() => {
            setEditandoUsuario(null);
            void clienteConsultas.invalidateQueries({ queryKey: ["usuarios"] });
          }}
        />
      )}

      {nuevoUsuario && (
        <DialogoUsuario
          alCerrar={() => setNuevoUsuario(false)}
          alFallar={setError}
          alHecho={() => {
            setNuevoUsuario(false);
            void clienteConsultas.invalidateQueries({ queryKey: ["usuarios"] });
          }}
        />
      )}

      {cerrandoSesiones && (
        <DialogoCerrarSesiones
          alCerrar={() => setCerrandoSesiones(false)}
          alFallar={setError}
          alSalir={salir}
        />
      )}
    </>
  );
}

/**
 * Echa a todo el equipo de todas las pantallas.
 *
 * Confirma antes porque no se puede deshacer y porque deja el local sin nadie
 * dentro: si el mostrador esta despachando, se encuentra la pantalla de entrada
 * a media entrega. Lo que hubiera sin enviar NO se pierde -se queda en la cola
 * de la tablet y sale en cuanto alguien vuelve a entrar-, pero el que esta
 * atendiendo se lleva el susto, asi que mas vale decirlo.
 *
 * Quien lo pulsa tambien sale, y por eso se llama a `salir()` en cuanto el
 * servidor contesta: su token ya no vale, y sin esto se quedaria con una
 * pantalla que falla en la siguiente consulta sin explicar por que.
 */
function DialogoCerrarSesiones({
  alCerrar,
  alFallar,
  alSalir,
}: {
  alCerrar: () => void;
  alFallar: (mensaje: string) => void;
  alSalir: () => Promise<void>;
}) {
  const { t } = useIdioma();
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("aj.cs.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton fantasma" onClick={alCerrar} disabled={enviando}>
            {t("general.cancelar")}
          </button>
          <button
            className="boton"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/club/usuarios/cerrar-sesiones", { metodo: "POST" });
                await alSalir();
              } catch (fallo) {
                setEnviando(false);
                alFallar(fallo instanceof Error ? fallo.message : t("aj.cs.fallo"));
                alCerrar();
              }
            }}
          >
            {enviando ? t("aj.cs.cerrando") : t("aj.cs.confirmar")}
          </button>
        </>
      }
    >
      <p>{t("aj.cs.queVaAPasar")}</p>
      <p className="suave pequeno">{t("aj.cs.loPendiente")}</p>
    </Modal>
  );
}

function DialogoUsuario({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState("TRABAJADOR");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("aj.u.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !nombre || !email || password.length < 8}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/club/usuarios", { metodo: "POST", cuerpo: { nombre, email, password, rol } });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("aj.u.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("aj.u.crear")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("aj.u.nombre")}>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
      </Campo>
      <Campo etiqueta={t("aj.u.email")}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Campo>
      <Campo etiqueta={t("aj.u.contrasena")} ayuda={t("aj.u.minimo")}>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Campo>
      <Campo etiqueta={t("aj.u.rol")}>
        <select value={rol} onChange={(e) => setRol(e.target.value)}>
          <option value="TRABAJADOR">{t("aj.u.trabajadorLargo")}</option>
        </select>
      </Campo>
    </Modal>
  );
}

function DialogoUsuarioEditar({
  usuario,
  alCerrar,
  alHecho,
  alFallar,
}: {
  usuario: Usuario;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const { usuario: yo } = useSesion();
  const [nombre, setNombre] = useState(usuario.nombre);
  const [rol, setRol] = useState(usuario.rol);
  const [activo, setActivo] = useState(usuario.activo);
  const [password, setPassword] = useState("");
  const [enviando, setEnviando] = useState(false);
  const soyYo = yo?.id === usuario.id;

  const catalogo = useQuery({ queryKey: ["permisos"], queryFn: () => api<PermisoCatalogo[]>("/club/permisos") });
  const puestos = useQuery({ queryKey: ["puestos"], queryFn: () => api<Puesto[]>("/club/puestos") });
  const [puestoId, setPuestoId] = useState(usuario.puestoId ?? "");
  const [extra, setExtra] = useState<string[]>(listaJson(usuario.permisosExtra));
  const [quitados, setQuitados] = useState<string[]>(listaJson(usuario.permisosQuitados));

  const porDefecto = (p: PermisoCatalogo) => (RANGO_ROL[rol] ?? 0) >= (RANGO_ROL[p.rolMinimo] ?? 99);
  const efectivo = (p: PermisoCatalogo) =>
    quitados.includes(p.clave) ? false : extra.includes(p.clave) ? true : porDefecto(p);

  // Solo se guarda la diferencia con el rol: si coincide, no es una excepción.
  function alternar(p: PermisoCatalogo) {
    const nuevo = !efectivo(p);
    setExtra((lista) => lista.filter((c) => c !== p.clave));
    setQuitados((lista) => lista.filter((c) => c !== p.clave));
    if (nuevo !== porDefecto(p)) {
      if (nuevo) setExtra((lista) => [...lista, p.clave]);
      else setQuitados((lista) => [...lista, p.clave]);
    }
  }

  return (
    <Modal
      titulo={t("aj.e.titulo", { nombre: usuario.nombre })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !nombre || (password !== "" && password.length < 8)}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/club/usuarios/${usuario.id}`, {
                  metodo: "PATCH",
                  cuerpo: {
                    nombre,
                    rol,
                    activo,
                    permisosExtra: extra,
                    permisosQuitados: quitados,
                    puestoId: puestoId || null,
                    ...(password ? { password } : {}),
                  },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("aj.e.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("g.guardar")}
          </button>
        </>
      }
    >
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        {usuario.email}
      </p>
      <Campo etiqueta={t("aj.e.nombre")}>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
      </Campo>
      <Campo etiqueta={t("aj.e.rol")}>
        <select value={rol} onChange={(e) => setRol(e.target.value)} disabled={soyYo}>
          <option value="TRABAJADOR">{t("rol.TRABAJADOR")}</option>
          <option value="PROPIETARIO">{t("rol.PROPIETARIO")}</option>
        </select>
      </Campo>

      {(puestos.data ?? []).length > 0 && (
        <Campo
          etiqueta={t("aj.e.puesto")}
          ayuda={t("aj.e.puestoAyuda")}
        >
          <select value={puestoId} onChange={(e) => setPuestoId(e.target.value)}>
            <option value="">{t("aj.e.sinPuesto")}</option>
            {(puestos.data ?? [])
              .filter((p) => p.activo || p.id === puestoId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
          </select>
        </Campo>
      )}

      <Campo etiqueta={t("aj.e.nuevaContrasena")} ayuda={t("aj.e.nuevaContrasenaAyuda")}>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
      </Campo>
      <label className="casilla">
        <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} disabled={soyYo} />
        <span>
          {t("aj.e.cuentaActiva")}
          <br />
          <span className="ayuda">{soyYo ? t("aj.e.noTuPropia") : t("aj.e.desactivada")}</span>
        </span>
      </label>

      <div style={{ marginTop: 4 }}>
        <div className="rotulo" style={{ marginBottom: 4 }}>
          {t("aj.e.permisos")}
        </div>
        <p className="ayuda" style={{ marginTop: 0 }}>
          {t("aj.e.permisosAyuda")}
        </p>
        <div className="columna" style={{ gap: 2, maxHeight: 260, overflowY: "auto" }}>
          {(catalogo.data ?? []).map((p) => {
            const cambiado = extra.includes(p.clave) || quitados.includes(p.clave);
            return (
              <label
                key={p.clave}
                className="fila separada pequeno"
                style={{
                  gap: 10,
                  padding: "5px 8px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: cambiado ? "var(--azul-suave)" : undefined,
                }}
              >
                <span className="fila" style={{ gap: 8 }}>
                  <input type="checkbox" checked={efectivo(p)} onChange={() => alternar(p)} />
                  {t(`permiso.${p.clave}` as Clave)}
                </span>
                <span className="suave">
                  {cambiado ? (efectivo(p) ? t("aj.e.concedido") : t("aj.e.retirado")) : ""}
                </span>
              </label>
            );
          })}
        </div>
        {(extra.length > 0 || quitados.length > 0) && (
          <button
            className="boton fantasma"
            onClick={() => {
              setExtra([]);
              setQuitados([]);
            }}
          >
            {t("aj.e.volverAlRol")}
          </button>
        )}
      </div>
    </Modal>
  );
}

function DialogoTipo({
  tipo,
  alCerrar,
  alHecho,
  alFallar,
}: {
  tipo: TipoSocio | null;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [datos, setDatos] = useState({
    nombre: tipo?.nombre ?? "",
    descripcion: tipo?.descripcion ?? "",
    descuentoPorcentaje: String(tipo?.descuentoPorcentaje ?? 0),
    cuotaAnualEuros: tipo?.cuotaAnualCentimos == null ? "" : String(tipo.cuotaAnualCentimos / 100),
    duracionMeses: String(tipo?.duracionMeses ?? 12),
    limiteDiarioG: tipo?.limiteDiarioMg == null ? "" : String(mgAGramos(tipo.limiteDiarioMg)),
    limiteMensualG: tipo?.limiteMensualMg == null ? "" : String(mgAGramos(tipo.limiteMensualMg)),
    color: tipo?.color ?? "#1e5f8e",
    orden: String(tipo?.orden ?? 0),
    activo: tipo?.activo ?? true,
  });
  const [enviando, setEnviando] = useState(false);
  const cambiar = (campo: string, valor: string | boolean) => setDatos((d) => ({ ...d, [campo]: valor }));

  async function guardar() {
    setEnviando(true);
    try {
      const cuerpo = {
        nombre: datos.nombre,
        descripcion: datos.descripcion || null,
        descuentoPorcentaje: Number(datos.descuentoPorcentaje || 0),
        cuotaAnualEuros: datos.cuotaAnualEuros === "" ? null : Number(datos.cuotaAnualEuros),
        duracionMeses: Number(datos.duracionMeses) || 12,
        limiteDiarioG: datos.limiteDiarioG === "" ? null : Number(datos.limiteDiarioG),
        limiteMensualG: datos.limiteMensualG === "" ? null : Number(datos.limiteMensualG),
        color: datos.color,
        orden: Number(datos.orden || 0),
        activo: datos.activo,
      };
      if (tipo) await api(`/tipos-socio/${tipo.id}`, { metodo: "PATCH", cuerpo });
      else await api("/tipos-socio", { metodo: "POST", cuerpo });
      alHecho();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("aj.t.fallo"));
      alCerrar();
    } finally {
      setEnviando(false);
    }
  }

  async function borrar() {
    if (!tipo || !window.confirm(`¿Borrar el tipo "${tipo.nombre}"?`)) return;
    try {
      await api(`/tipos-socio/${tipo.id}`, { metodo: "DELETE" });
      alHecho();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("aj.t.falloBorrar"));
      alCerrar();
    }
  }

  return (
    <Modal
      titulo={tipo ? t("aj.t.editar", { nombre: tipo.nombre }) : t("aj.t.nuevo")}
      alCerrar={alCerrar}
      acciones={
        <>
          {tipo && tipo._count.socios === 0 && (
            <button className="boton fantasma" onClick={borrar} style={{ marginRight: "auto", color: "var(--rojo)" }}>
              {t("aj.t.borrar")}
            </button>
          )}
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button className="boton" onClick={guardar} disabled={enviando || !datos.nombre}>
            {enviando ? t("g.guardando") : t("g.guardar")}
          </button>
        </>
      }
    >
      <div className="linea-campos">
        <Campo etiqueta={t("aj.t.nombre")}>
          <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} autoFocus />
        </Campo>
        <Campo etiqueta={t("aj.t.color")}>
          <input type="color" value={datos.color} onChange={(e) => cambiar("color", e.target.value)} style={{ height: 42 }} />
        </Campo>
      </div>
      <Campo etiqueta={t("aj.t.descripcion")}>
        <input value={datos.descripcion} onChange={(e) => cambiar("descripcion", e.target.value)} />
      </Campo>
      <div className="linea-campos">
        <Campo etiqueta={t("aj.t.descuento")} ayuda={t("aj.t.descuentoAyuda")}>
          <input type="number" min={0} max={100} value={datos.descuentoPorcentaje} onChange={(e) => cambiar("descuentoPorcentaje", e.target.value)} />
        </Campo>
        <Campo etiqueta={t("aj.t.cuota")} ayuda={t("aj.t.cuotaAyuda")}>
          <input type="number" min={0} step="0.01" value={datos.cuotaAnualEuros} onChange={(e) => cambiar("cuotaAnualEuros", e.target.value)} />
        </Campo>
        <Campo
          etiqueta={t("aj.t.duracion")}
          ayuda={t("aj.t.duracionAyuda")}
        >
          <input
            type="number"
            min={1}
            max={120}
            value={datos.duracionMeses}
            onChange={(e) => cambiar("duracionMeses", e.target.value)}
          />
        </Campo>
      </div>
      <div className="linea-campos">
        <Campo etiqueta={t("aj.t.limiteDiario")} ayuda={t("aj.t.limiteDiarioAyuda")}>
          <input type="number" min={0} step="0.5" value={datos.limiteDiarioG} onChange={(e) => cambiar("limiteDiarioG", e.target.value)} />
        </Campo>
        <Campo etiqueta={t("aj.t.limiteMensual")} ayuda={t("aj.t.limiteDiarioAyuda")}>
          <input type="number" min={0} step="1" value={datos.limiteMensualG} onChange={(e) => cambiar("limiteMensualG", e.target.value)} />
        </Campo>
      </div>
      <div className="linea-campos">
        <Campo etiqueta={t("aj.t.orden")}>
          <input type="number" min={0} value={datos.orden} onChange={(e) => cambiar("orden", e.target.value)} />
        </Campo>
      </div>
      <label className="casilla">
        <input type="checkbox" checked={datos.activo} onChange={(e) => cambiar("activo", e.target.checked)} />
        <span>{t("aj.t.activo")}</span>
      </label>
    </Modal>
  );
}


/**
 * Los puntos de cobro del local: el mostrador, la barra, la puerta.
 *
 * Un club que no cree ninguno sigue funcionando exactamente como antes: una
 * caja, y todo dentro. Por eso el panel explica para qué sirven en vez de dar
 * por hecho que hacen falta.
 */
function PuestosDeTrabajo({
  puestos,
  alHecho,
  alFallar,
}: {
  puestos: Puesto[];
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t, cuenta } = useIdioma();
  const [nombre, setNombre] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function crear() {
    if (!nombre.trim()) return;
    setEnviando(true);
    try {
      await api("/club/puestos", { metodo: "POST", cuerpo: { nombre } });
      setNombre("");
      alHecho();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("aj.p.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Panel titulo={t("aj.p.titulo")}>
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        {t("aj.p.nota")}
      </p>
      <p className="suave pequeno">{t("aj.p.nota2")}</p>

      {puestos.length > 0 && (
        <table className="tabla">
          <tbody>
            {puestos.map((p) => (
              <tr key={p.id} style={p.activo ? undefined : { opacity: 0.55 }}>
                <td>
                  <strong>{p.nombre}</strong>
                  {!p.activo && <span className="suave pequeno">{t("aj.p.inactivo")}</span>}
                </td>
                <td className="num suave pequeno">
                  {cuenta(p._count.usuarios, "aj.p.unaPersona", "aj.p.personas")}
                </td>
                <td style={{ textAlign: "right" }}>
                  <button
                    className="boton fantasma"
                    onClick={async () => {
                      try {
                        await api(`/club/puestos/${p.id}`, { metodo: "PATCH", cuerpo: { activo: !p.activo } });
                        alHecho();
                      } catch (fallo) {
                        alFallar(fallo instanceof Error ? fallo.message : t("aj.p.falloCambiar"));
                      }
                    }}
                  >
                    {p.activo ? t("aj.p.desactivar") : t("aj.p.activar")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="fila" style={{ marginTop: 12 }}>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={t("aj.p.ejemplo")}
          maxLength={60}
        />
        <button className="boton secundario" disabled={enviando || !nombre.trim()} onClick={crear}>
          {t("aj.p.anadir")}
        </button>
      </div>
    </Panel>
  );
}
